import { lazy, Suspense } from "react";
import { Navigate, RouteObject } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SiteLayout from "../components/layout/SiteLayout";
import MobileLayout from "../components/layout/MobileLayout";
import PageTransition from "../components/feature/PageTransition";
import AdminLayout from "../components/layout/AdminLayout";
import { AVISO_INSTRUTOR_SEM_AREA, PARAM_AVISO_AREA } from "../lib/navegacao";

const AdminRastreamentoPage = lazy(() => import("../pages/admin/AdminRastreamentoPage"));



function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#07070D] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}


const HomePage = lazy(() => import("../pages/home/page"));
const CoursePage = lazy(() => import("../pages/course/page"));
const CourseSlugPage = lazy(() => import("../pages/course/CourseSlugPage"));
const DashboardPage = lazy(() => import("../pages/dashboard/page"));
const EbookPage = lazy(() => import("../pages/ebook/page"));
const EbookSlugPage = lazy(() => import("../pages/ebook/EbookSlugPage"));
const LinksPage = lazy(() => import("../pages/links/page"));
const NotFoundPage = lazy(() => import("../pages/NotFound"));
const ValidarCertificadoPage = lazy(() => import("../pages/validar-certificado/page"));
const CheckoutPage = lazy(() => import("../pages/checkout/page"));
const CheckoutSuccessPage = lazy(() => import("../pages/checkout/SuccessPage"));
const AdminUploadPage = lazy(() => import("../pages/admin/upload/page"));
const SilexPage = lazy(() => import("../pages/mentoria/SilexPage"));
const MentoriaSuccessPage = lazy(() => import("../pages/mentoria/MentoriaSuccessPage"));
const CursosPage = lazy(() => import("../pages/cursos/page"));
const SobrePage = lazy(() => import("../pages/sobre/page"));
const FaqPage = lazy(() => import("../pages/faq/page"));
const TermosPage = lazy(() => import("../pages/termos/page"));
const PrivacidadePage = lazy(() => import("../pages/privacidade/page"));
const ContatoPage = lazy(() => import("../pages/contato/page"));
const CourseHomePage = lazy(() => import("../pages/curso-home/page"));
const AulaPage = lazy(() => import("../pages/aula/page"));
const MeuMetodoSilexPage = lazy(() => import("../pages/meu-metodo-silex/page"));
const AvaliacaoPage = lazy(() => import("../pages/avaliacao/page"));
const AvaliacoesPage = lazy(() => import("../pages/dashboard/avaliacoes/page"));
const PerfilPage = lazy(() => import("../pages/dashboard/perfil/page"));
const NotesPage = lazy(() => import("../pages/dashboard/notas/page"));
const CertificadosPage = lazy(() => import("../pages/certificados/page"));
const AdminCursosPage = lazy(() => import("../pages/admin/cursos/page"));
const AdminSilexTrilhaPage = lazy(() => import("../pages/admin/silex/trilha/page"));
// Preview de tokens de cor. Fora do menu de propósito: é instrumento de decisão
// de design, não ferramenta de operação. Chega pela URL.
const AdminPreviewCoresPage = lazy(() => import("../pages/admin/preview-cores/page"));
const AdminAlunosPage = lazy(() => import("../pages/admin/alunos/page"));
const RankingPage = lazy(() => import("../pages/ranking/page"));
const ComunidadePage = lazy(() => import("../pages/comunidade/page"));
const SuccessUpsellPage = lazy(() => import("../pages/checkout/SuccessUpsell"));
const RetencaoPage = lazy(() => import("../pages/admin/retencao/page"));
const QuizPage = lazy(() => import("../pages/quiz/page"));
const WebinarsPage = lazy(() => import("../pages/webinars/page"));
const AdminWebinarsPage = lazy(() => import("../pages/admin/webinars/page"));
const AdminPedidosPage = lazy(() => import("../pages/admin/pedidos/page"));
const AdminMentoriaSlotsPage = lazy(() => import("../pages/admin/mentoria-slots/page"));
const AdminSetupPage = lazy(() => import("../pages/admin/setup/page"));
const AdminDashboardPage = lazy(() => import("../pages/admin-dashboard/page"));
const MeuPerfilPage = lazy(() => import("../pages/meu-perfil/page"));
const ProgressoPage = lazy(() => import("../pages/progresso/page"));
const WebinarLivePage = lazy(() => import("../pages/webinar-live/page"));
const DashboardPedidosPage = lazy(() => import("../pages/dashboard/pedidos/page"));
const HistoricoProgressoPage = lazy(() => import("../pages/dashboard/historico/page"));
const LivrosPage = lazy(() => import("../pages/livros/page"));
const WebinarDetailPage = lazy(() => import("../pages/webinars/WebinarDetailPage"));
const InstructorPage = lazy(() => import("../pages/instructor/InstructorPage"));
const CarrinhoPage = lazy(() => import("../pages/carrinho/page"));
const ConquistasPage = lazy(() => import("../pages/conquistas/page"));
const AdminCuponsPage = lazy(() => import("../pages/admin/cupons/page"));
const AdminChurnPage = lazy(() => import("../pages/admin/churn/page"));
const AdminAvaliacoesPage = lazy(() => import("../pages/admin/avaliacoes/page"));
const LivroDigitalSuccessPage = lazy(() => import("../pages/livro/LivroDigitalSuccessPage"));
const LivroFisicoSuccessPage = lazy(() => import("../pages/livro/LivroFisicoSuccessPage"));

import { useState, useEffect } from 'react';
import { shouldUseMobileLayout } from '../utils/deviceDetection';
import AvisoPerfilSemArea from '../components/feature/AvisoPerfilSemArea';

function LayoutWrapper({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState<boolean>(() => shouldUseMobileLayout());

  useEffect(() => {
    const handler = () => setIsMobile(shouldUseMobileLayout());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // O aviso fica FORA do PageTransition: ele explica por que a pessoa foi parar
  // aqui, e não pode entrar e sair junto com a animação de troca de página.
  const conteudo = (
    <>
      <AvisoPerfilSemArea />
      <PageTransition>{children}</PageTransition>
    </>
  );

  if (isMobile) {
    return <MobileLayout>{conteudo}</MobileLayout>;
  }
  return <SiteLayout>{conteudo}</SiteLayout>;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <HomePage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/cursos",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <CursosPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/curso",
    element: (
      <Suspense fallback={<div />}>
        <CoursePage />
      </Suspense>
    ),
  },
  {
    path: "/curso/:slug",
    element: (
      <Suspense fallback={<div />}>
        <CourseSlugPage />
      </Suspense>
    ),
  },
  {
    path: "/ebook",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <EbookPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/ebook/:slug",
    element: (
      <Suspense fallback={<div />}>
        <EbookSlugPage />
      </Suspense>
    ),
  },
  {
    path: "/links",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <LinksPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<div />}>
          <DashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "/dashboard/avaliacoes",
    element: (
      <Suspense fallback={<div />}>
        <AvaliacoesPage />
      </Suspense>
    ),
  },
  {
    path: "/dashboard/perfil",
    element: (
      <Suspense fallback={<div />}>
        <PerfilPage />
      </Suspense>
    ),
  },
  {
    path: "/dashboard/notas",
    element: (
      <Suspense fallback={<div />}>
        <NotesPage />
      </Suspense>
    ),
  },
  {
    path: "/dashboard/pedidos",
    element: (
      <Suspense fallback={<div />}>
        <DashboardPedidosPage />
      </Suspense>
    ),
  },
  {
    path: "/dashboard/historico",
    element: (
      <Suspense fallback={<div />}>
        <HistoricoProgressoPage />
      </Suspense>
    ),
  },
  {
    path: "/curso-home/:courseId",
    element: (
      <Suspense fallback={<div />}>
        <CourseHomePage />
      </Suspense>
    ),
  },
  {
    path: "/aula/:courseId/:lessonId",
    element: (
      <Suspense fallback={<div />}>
        <AulaPage />
      </Suspense>
    ),
  },
  {
    path: "/meu-metodo-silex",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<div />}>
          <MeuMetodoSilexPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "/avaliacao/:moduleId",
    element: (
      <Suspense fallback={<div />}>
        <AvaliacaoPage />
      </Suspense>
    ),
  },
  {
    path: "/certificados",
    element: (
      <Suspense fallback={<div />}>
        <CertificadosPage />
      </Suspense>
    ),
  },
  {
    path: "/validar-certificado",
    element: (
      <Suspense fallback={<div />}>
        <ValidarCertificadoPage />
      </Suspense>
    ),
  },
  {
    path: "/checkout",
    element: <Suspense fallback={<div />}><CheckoutPage /></Suspense>,
  },
  {
    path: "/checkout/sucesso",
    element: <Suspense fallback={<div />}><CheckoutSuccessPage /></Suspense>,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PAINEL ADMINISTRATIVO
  // Todas as rotas do painel — inclusive a home /admin — ficam aninhadas sob o
  // AdminLayout, que fornece o ÚNICO menu lateral e a proteção de acesso
  // (sessão + papel admin). As páginas entram no <Outlet /> do layout.
  // O menu é agrupado por domínio de negócio; ver NAV_ITEMS em AdminLayout.tsx.
  // Obs.: /admin-dashboard virou redirect para /admin (rota legada, mais abaixo).
  // ─────────────────────────────────────────────────────────────────────────
  {
    element: <AdminLayout />,
    children: [
      // Home do painel. Toda rota /admin/* herda a guarda do AdminLayout
      // (sessão + papel admin, AdminLayout.tsx:60-97).
      { path: "/admin", element: <Suspense fallback={<div />}><AdminDashboardPage /></Suspense> },
      { path: "/admin/silex/trilha", element: <Suspense fallback={<div />}><AdminSilexTrilhaPage /></Suspense> },
      { path: "/admin/preview-cores", element: <Suspense fallback={<div />}><AdminPreviewCoresPage /></Suspense> },
      { path: "/admin/upload", element: <Suspense fallback={<div />}><AdminUploadPage /></Suspense> },
      { path: "/admin/cursos", element: <Suspense fallback={<div />}><AdminCursosPage /></Suspense> },
      { path: "/admin/alunos", element: <Suspense fallback={<div />}><AdminAlunosPage /></Suspense> },
      { path: "/admin/retencao", element: <Suspense fallback={<div />}><RetencaoPage /></Suspense> },
      { path: "/admin/rastreamento", element: <Suspense fallback={<div />}><AdminRastreamentoPage /></Suspense> },
      { path: "/admin/webinars", element: <Suspense fallback={<div />}><AdminWebinarsPage /></Suspense> },
      { path: "/admin/pedidos", element: <Suspense fallback={<div />}><AdminPedidosPage /></Suspense> },
      { path: "/admin/setup", element: <Suspense fallback={<div />}><AdminSetupPage /></Suspense> },
      { path: "/admin/mentoria-slots", element: <Suspense fallback={<div />}><AdminMentoriaSlotsPage /></Suspense> },
      { path: "/admin/cupons", element: <Suspense fallback={<div />}><AdminCuponsPage /></Suspense> },
      { path: "/admin/churn", element: <Suspense fallback={<div />}><AdminChurnPage /></Suspense> },
      { path: "/admin/avaliacoes", element: <Suspense fallback={<div />}><AdminAvaliacoesPage /></Suspense> },
    ],
  },

  {
    path: "/quiz/:quizId",
    element: <Suspense fallback={<div />}><QuizPage /></Suspense>,
  },
  {
    path: "/webinars",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}><WebinarsPage /></Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/webinars/:id",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}><WebinarDetailPage /></Suspense>
      </LayoutWrapper>
    ),
  },
  {
    // Rota legada. O painel agora é /admin, dentro do AdminLayout (sidebar único).
    // Mantida como redirect porque AuthContext ainda manda o admin para cá no login.
    path: "/admin-dashboard",
    element: <Navigate to="/admin" replace />,
  },
  {
    // FECHADO em 2026-08-03. O painel do instrutor ficava aberto a qualquer
    // visitante (sem ProtectedRoute e sem checagem de papel no próprio arquivo)
    // e é onde vive o upload para os buckets lesson-videos / lesson-materials.
    //
    // O AuthContext não manda mais ninguém para cá — quem tem role 'instructor'
    // vai para a home pública COM AVISO (ver homeDoPerfil). A rota permanece só
    // para links antigos, e o destino deixou de ser /dashboard: mandar instrutor
    // para a área de aluno era exatamente o bug. Os arquivos em
    // pages/instructor-dashboard/ seguem no repo e voltam com a área nova.
    path: "/instructor-dashboard",
    element: <Navigate to={`/?${PARAM_AVISO_AREA}=${AVISO_INSTRUTOR_SEM_AREA}`} replace />,
  },
  {
    path: "/meu-perfil",
    element: <Suspense fallback={<div />}><MeuPerfilPage /></Suspense>,
  },
  {
    path: "/progresso",
    element: <Suspense fallback={<div />}><ProgressoPage /></Suspense>,
  },
  {
    path: "/webinar/:id",
    element: <Suspense fallback={<div />}><WebinarLivePage /></Suspense>,
  },
  {
    path: "/ranking",
    element: <Suspense fallback={<div />}><RankingPage /></Suspense>,
  },
  {
    path: "/comunidade",
    element: <Suspense fallback={<div />}><ComunidadePage /></Suspense>,
  },
  {
    path: "/checkout/upsell",
    element: <Suspense fallback={<div />}><SuccessUpsellPage /></Suspense>,
  },
  // ─────────────────────────────────────────────────────────────────────────
  // MENTORIA SÍLEX
  // /mentoria é a rota canônica da página de vendas SÍLEX. As rotas do funil
  // antigo viram redirects com `replace` (não empilham a rota morta no
  // histórico). A rota exata /mentoria fica declarada ANTES de /mentoria/:slug
  // por robustez — o ranking do react-router v7 já prioriza segmento estático,
  // mas a ordem explícita evita qualquer ambiguidade na leitura.
  // ─────────────────────────────────────────────────────────────────────────
  {
    path: "/mentoria",
    element: (
      <Suspense fallback={<div />}>
        <SilexPage />
      </Suspense>
    ),
  },
  {
    path: "/mentoria/:slug",
    element: <Navigate to="/mentoria" replace />,
  },
  {
    path: "/mentoria-silex",
    element: <Navigate to="/mentoria" replace />,
  },
  {
    path: "/mentoria-estrategica",
    element: <Navigate to="/mentoria" replace />,
  },
  {
    path: "/qualificacao",
    element: <Navigate to="/mentoria" replace />,
  },
  {
    path: "/turmas",
    element: <Navigate to="/mentoria" replace />,
  },
  {
    path: "/waitlist",
    element: <Navigate to="/mentoria" replace />,
  },
  {
    path: "/checkout/sucesso-mentoria",
    element: (
      <Suspense fallback={<div />}>
        <MentoriaSuccessPage />
      </Suspense>
    ),
  },
  {
    path: "/checkout/sucesso-livro-digital",
    element: (
      <Suspense fallback={<div />}>
        <LivroDigitalSuccessPage />
      </Suspense>
    ),
  },
  {
    path: "/checkout/sucesso-livro-fisico",
    element: (
      <Suspense fallback={<div />}>
        <LivroFisicoSuccessPage />
      </Suspense>
    ),
  },
  {
    path: "/sobre",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <SobrePage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/faq",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <FaqPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/termos-de-uso",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <TermosPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/politica-de-privacidade",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <PrivacidadePage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/contato",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <ContatoPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/livros",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <LivrosPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/instructor/:slug",
    element: (
      <LayoutWrapper>
        <Suspense fallback={<div />}>
          <InstructorPage />
        </Suspense>
      </LayoutWrapper>
    ),
  },
  {
    path: "/carrinho",
    element: <Suspense fallback={<div />}><CarrinhoPage /></Suspense>,
  },
  {
    path: "/conquistas",
    element: <Suspense fallback={<div />}><ConquistasPage /></Suspense>,
  },
  {
    path: "/login",
    element: <Navigate to="/" replace />,
  },
  {
    path: "*",
    element: (
      <Suspense fallback={<div />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];

export default routes;
